import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import PermissionResourceSchema from "permissions/resources/_schemas/permission-resource-schema";
import { AccessLevel, ResourceType, Scope } from "permissions/resources/permission-resource-types";
import { z } from "zod";

export type MarketplaceSearchAccessJSON = z.infer<typeof MarketplaceSearchAccessSchema.MarketplaceSearchAccessJSONZ>;

export type MarketplaceSearchAccessParams = z.infer<typeof MarketplaceSearchAccessSchema.MarketplaceSearchAccessZ>;

export default class MarketplaceSearchAccessSchema {
  public static readonly MarketplaceSearchAccessJSONZ = z.object({
    ...PermissionResourceSchema.ResourceJSONZ.shape,
    resourceType: z.literal(ResourceType.MarketplaceSearchAccess),
    accessLevel: z.enum(AccessLevel).nullish(),
    scope: z.enum(Scope).nullish(),
  });

  public static readonly MarketplaceSearchAccessZ = z.object({
    ...PermissionResourceSchema.ResourceZ.shape,
    resourceType: z.literal(ResourceType.MarketplaceSearchAccess),
    accessLevel: z.enum(AccessLevel).optional(),
    scope: z.enum(Scope).optional(),
  });

  public static validate(values: unknown): MarketplaceSearchAccessParams {
    return parseOrThrow(MarketplaceSearchAccessSchema.MarketplaceSearchAccessZ, values, {
      logError: true,
      schemaName: "MarketplaceSearchAccess",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): MarketplaceSearchAccessJSON {
    return parseOrThrow(MarketplaceSearchAccessSchema.MarketplaceSearchAccessJSONZ, values, {
      logError: true,
      schemaName: "MarketplaceSearchAccess",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is MarketplaceSearchAccessParams {
    try {
      MarketplaceSearchAccessSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is MarketplaceSearchAccessJSON {
    try {
      MarketplaceSearchAccessSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: MarketplaceSearchAccessJSON): MarketplaceSearchAccessParams {
    const parsed = parseOrThrow(MarketplaceSearchAccessSchema.MarketplaceSearchAccessJSONZ, data, {
      logError: true,
      schemaName: "MarketplaceSearchAccess",
      operation: "deserialize",
      payloadSample: data,
    });
    return MarketplaceSearchAccessSchema.codec.decode(parsed);
  }

  public static serialize(notification: MarketplaceSearchAccessParams): MarketplaceSearchAccessJSON {
    const parsed = MarketplaceSearchAccessSchema.validate(notification);
    return MarketplaceSearchAccessSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    MarketplaceSearchAccessSchema.MarketplaceSearchAccessJSONZ,
    MarketplaceSearchAccessSchema.MarketplaceSearchAccessZ,
    {
      decode: (data) => ({
        ...PermissionResourceSchema.deserialize(data),
        resourceType: ResourceType.MarketplaceSearchAccess as const,
        accessLevel: data.accessLevel ?? undefined,
        scope: data.scope ?? undefined,
      }),
      encode: (resource) => ({
        ...PermissionResourceSchema.serialize(resource),
        resourceType: ResourceType.MarketplaceSearchAccess as const,
        accessLevel: resource.accessLevel,
        scope: resource.scope,
      }),
    }
  );
}
